// src/App.tsx
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import "./index.css";

import AuthGuard from "@/app/guard/AuthGuard";

import Login from "@/pages/auth/Login";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import Otp from "@/pages/auth/Otp";
import SelectPortal from "@/pages/auth/SelectPortal";
import ChangePassword from "@/pages/account/ChangePassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import MyAccount from "@/pages/account/MyAccount";

import ViewRoles from "@/pages/roles/ViewRoles";
import ViewModules from "@/pages/modules/ViewModules";
import ViewEmailTemplates from "@/pages/email-templates/ViewEmailTemplates";
import ViewEmailTemplateDetail from "@/pages/email-templates/ViewEmailTemplateDetail";

import Dashboard from "@/pages/dashboard/Index";

import ViewUsers from "@/pages/users/ViewUsers";
import CreateUserPage from "@/pages/users/CreateUserPage";
import EditUserPage from "@/pages/users/EditUserPage";
import UserPermissions from "@/pages/users/UserPermissions";

import ViewProducts from "@/pages/products/ViewProducts";
import ViewPromos from "@/pages/promos/ViewPromos";
import CreatePromoPage from "@/pages/promos/CreatePromoPage";
import EditPromoPage from "@/pages/promos/EditPromoPage";
import ViewPaymentLinks from "@/pages/payment-links/ViewPaymentLinks";
import PermissionGuard from "@/app/guard/PermissionGuard";

import ViewSuppliers from "@/pages/suppliers/ViewSuppliers";
import CreateSupplierPage from "@/pages/suppliers/CreateSupplierPage";
import EditSupplierPage from "@/pages/suppliers/EditSupplierPage";
import ViewAffiliatePartners from "@/pages/affiliates/ViewAffiliatePartners";
import CreateAffiliatePartnerPage from "@/pages/affiliates/CreateAffiliatePartnerPage";
import EditAffiliatePartnerPage from "@/pages/affiliates/EditAffiliatePartnerPage";

import ViewCustomers from "@/pages/customers/ViewCustomers";
import CreateCustomerPage from "@/pages/customers/CreateCustomerPage";
import EditCustomerPage from "@/pages/customers/EditCustomerPage";

import ViewLeads from "@/pages/leads/ViewLeads";
import CreateLeadPage from "@/pages/leads/CreateLeadPage";
import EditLeadPage from "@/pages/leads/EditLeadPage";

import ViewTourTypes from "@/pages/tour-types/ViewTourTypes";

import TourPackageCreatePage from "@/pages/tour-packages/TourPackageCreatePage";
import ViewTourPackages from "@/pages/tour-packages/ViewTourPackages";
import TourPackageEditPage from "@/pages/tour-packages/TourPackageEditPage";

import AdminBookingPage from "@/pages/bookings/AdminBookingPage";
import AdminBookingDetailsPage from "@/pages/bookings/AdminBookingDetailsPage";
import AdminHotelBookingsPage from "@/pages/hotel-bookings/AdminHotelBookingsPage";
import AdminHotelBookingDetailsPage from "@/pages/hotel-bookings/AdminHotelBookingDetailsPage";
import HotelCatalogueCoveragePage from "@/pages/hotel-catalogue/HotelCatalogueCoveragePage";
import ActivityLogsPage from "@/pages/activity-logs/ActivityLogsPage";

import LocationsPage from "@/pages/locations/LocationsPage";
import CreateCountryPage from "@/pages/locations/countries/CreateCountryPage";
import EditCountryPage from "@/pages/locations/countries/EditCountryPage";
import CreateRegionPage from "@/pages/locations/regions/CreateRegionPage";
import EditRegionPage from "@/pages/locations/regions/EditRegionPage";
import CreateCityPage from "@/pages/locations/cities/CreateCityPage";
import EditCityPage from "@/pages/locations/cities/EditCityPage";

import ViewCurrency from "@/pages/currency/ViewCurrency";
import ViewAirlines from "@/pages/flights/airlines/ViewAirlines";
import ViewAirports from "@/pages/flights/airports/ViewAirports";

import Forbidden from "@/pages/errors/Forbidden";
import { getAuth } from "@/api/client";

/**
 * Admin-only roles
 */
const ADMIN_ROLE_NAMES = new Set([
  "Master Admin",
  "Admin",
  "Manager",
  "Employee",
  "Supplier",
  "Affiliate Partner",
]);

function normRole(v: unknown) {
  return String(v ?? "").trim();
}

/**
 * AdminGuard (local)
 * - Must be used INSIDE <AuthGuard />
 * - Blocks customers/non-admin users from admin URLs
 * - Redirects to /403 (preferred)
 */
function AdminGuard() {
  const a = getAuth();
  const user: any = a?.user ?? null;

  const roleName = normRole(user?.roleName ?? user?.role?.name ?? user?.role_name);
  const ok = !!roleName && ADMIN_ROLE_NAMES.has(roleName);

  if (!ok) return <Navigate to="/403" replace />;

  return <Outlet />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/otp" element={<Otp />} />
        <Route path="/select-portal" element={<SelectPortal />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* 403 */}
        <Route path="/403" element={<Forbidden />} />

        {/* Auth required */}
        <Route element={<AuthGuard />}>
          {/* Accessible to ALL logged-in users (including customers) */}
          <Route path="/account" element={<MyAccount />} />
          <Route path="/account/change-password" element={<ChangePassword />} />

          {/* Admin-only area */}
          <Route element={<AdminGuard />}>
            <Route path="/dashboard" element={<Dashboard />} />

            <Route path="/roles" element={<ViewRoles />} />
            <Route path="/modules" element={<ViewModules />} />
            <Route path="/email-templates" element={<ViewEmailTemplates />} />
            <Route path="/email-templates/:key" element={<ViewEmailTemplateDetail />} />

            <Route path="/hr/users" element={<ViewUsers />} />
            <Route path="/hr/users/new" element={<CreateUserPage />} />
            <Route path="/hr/users/:id/edit" element={<EditUserPage />} />
            <Route path="/hr/permissions" element={<UserPermissions />} />

            <Route path="/products" element={<ViewProducts />} />
            {/* Gated by the "promos" RBAC capability — mirrors the backend
                requirePermission("promos"), so no page loads without the grant. */}
            <Route element={<PermissionGuard module="promos" />}>
              <Route path="/promos" element={<ViewPromos />} />
              <Route path="/promos/new" element={<CreatePromoPage />} />
              <Route path="/promos/edit/:id" element={<EditPromoPage />} />
            </Route>

            {/* Gated by the "payment-links" RBAC capability — mirrors the backend
                requirePermission("payment-links"). */}
            <Route element={<PermissionGuard module="payment-links" />}>
              <Route path="/payment-links" element={<ViewPaymentLinks />} />
            </Route>

            <Route path="/suppliers" element={<ViewSuppliers />} />
            <Route path="/suppliers/new" element={<CreateSupplierPage />} />
            <Route path="/suppliers/edit/:id" element={<EditSupplierPage />} />
            <Route path="/affiliates" element={<ViewAffiliatePartners />} />
            <Route path="/affiliates/new" element={<CreateAffiliatePartnerPage />} />
            <Route path="/affiliates/edit/:id" element={<EditAffiliatePartnerPage />} />

            <Route path="/customers" element={<ViewCustomers />} />
            <Route path="/customers/new" element={<CreateCustomerPage />} />
            <Route path="/customers/:id/edit" element={<EditCustomerPage />} />

            <Route path="/leads" element={<ViewLeads />} />
            <Route path="/leads/create" element={<CreateLeadPage />} />
            <Route path="/leads/:id/edit" element={<EditLeadPage />} />

            <Route path="/tour/tour-types" element={<ViewTourTypes />} />
            <Route path="/tour/tour-packages/new" element={<TourPackageCreatePage />} />
            <Route path="/tour/tour-packages" element={<ViewTourPackages />} />
            <Route path="/tour/tour-packages/edit/:id" element={<TourPackageEditPage />} />

            <Route path="/bookings" element={<AdminBookingPage />} />
            <Route path="/bookings/:id" element={<AdminBookingDetailsPage />} />
            <Route path="/flight-bookings" element={<AdminBookingPage />} />
            <Route path="/flight-bookings/:id" element={<AdminBookingDetailsPage />} />
            <Route path="/hotel-bookings" element={<AdminHotelBookingsPage />} />
            <Route path="/hotel-bookings/:id" element={<AdminHotelBookingDetailsPage />} />
            <Route path="/hotel-catalogue" element={<HotelCatalogueCoveragePage />} />
            <Route path="/flights/activity-logs" element={<ActivityLogsPage />} />

            {/* Countries / Regions / Cities are tabs of one Locations page */}
            <Route path="/locations" element={<LocationsPage />} />
            <Route path="/locations/countries/new" element={<CreateCountryPage />} />
            <Route path="/locations/countries/edit/:iso2" element={<EditCountryPage />} />
            <Route path="/locations/regions/new" element={<CreateRegionPage />} />
            <Route path="/locations/regions/edit/:id" element={<EditRegionPage />} />
            <Route path="/locations/cities/new" element={<CreateCityPage />} />
            <Route path="/locations/cities/edit/:id" element={<EditCityPage />} />
            <Route path="/locations/:entity" element={<LocationsPage />} />

            <Route path="/currencies" element={<ViewCurrency />} />
            <Route path="/flights/airlines" element={<ViewAirlines />} />
            <Route path="/flights/airports" element={<ViewAirports />} />
          </Route>
        </Route>

        {/* Catch-all should be last */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
